/**
 * BoardModeSwitcher — small popover in the top-right tools cluster
 * that lets the player switch between board renderers without leaving
 * the match. The renderers all consume the same PublicTableState so
 * the swap is instant and state-preserving.
 *
 * Popover rendering note: the surrounding chrome (Fullscreen2D's
 * header, Classic's relative root) creates new stacking contexts via
 * backdrop-blur / transform. A normal absolute child gets trapped
 * inside that context and can render BEHIND sibling boards. To
 * escape, the popover uses createPortal into document.body and
 * positions itself via the button's getBoundingClientRect.
 */

import { type ReactElement, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BOARD_MODES, type BoardMode, useBoardMode } from '../board-modes.js';

const ICON: Record<BoardMode, string> = {
  classic: '▦',
  fullscreen2d: '⛶',
  carousel: '◀▣▶',
  overhead3d: '◇',
  broadcast: '📺',
};

export function BoardModeSwitcher(): ReactElement {
  const { mode, setMode } = useBoardMode();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [popoverPos, setPopoverPos] = useState<{ top: number; right: number } | null>(null);

  // Position the popover under the button using viewport coords (since
  // it's portaled to body and uses position: fixed).
  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setPopoverPos({
      top: rect.bottom + 4,
      right: window.innerWidth - rect.right,
    });
  }, [open]);

  // Click-outside to close.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      const inButton = buttonRef.current?.contains(target ?? null);
      const inPopover = target instanceof Element && target.closest('[data-board-mode-popover]');
      if (!inButton && !inPopover) setOpen(false);
    };
    window.addEventListener('pointerdown', onPointerDown, true);
    return () => window.removeEventListener('pointerdown', onPointerDown, true);
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={`Board view (current: ${mode})`}
        className="rounded border border-cosmos-700 bg-cosmos-900/80 px-2 py-1 text-xs text-cosmos-200 hover:bg-cosmos-800"
      >
        {ICON[mode]} View
      </button>
      {open && popoverPos
        ? createPortal(
            <div
              data-board-mode-popover
              role="menu"
              style={{
                position: 'fixed',
                top: popoverPos.top,
                right: popoverPos.right,
                // z-index high enough to escape any reasonable stacking
                // context the app might create later.
                zIndex: 9999,
              }}
              className="w-56 rounded border border-cosmos-700 bg-cosmos-900 p-1 shadow-2xl ring-1 ring-cosmos-600"
            >
              {BOARD_MODES.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => {
                    setMode(m.id);
                    setOpen(false);
                  }}
                  className={`flex w-full items-start gap-2 rounded px-2 py-1.5 text-left text-xs ${
                    mode === m.id ? 'bg-amber-900/30' : 'hover:bg-cosmos-800'
                  }`}
                >
                  <span className="font-mono text-cosmos-400">{ICON[m.id]}</span>
                  <span className="flex-1">
                    <span className="block font-semibold text-cosmos-100">{m.label}</span>
                    <span className="block text-[11px] text-cosmos-400">{m.hint}</span>
                  </span>
                  {mode === m.id ? <span className="text-amber-400">●</span> : null}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
